<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-spacer></v-spacer> 
                    <v-menu v-if="!loading" left v-model="menu_status" offset-y > 
                        <template v-slot:activator="{ on }">
                            <v-btn color="primary" dark v-on="on" :loading="loading">
                                <span class="ml-7">{{ selectedTeamData.name }}</span>
                                <v-icon v-show="!menu_status">arrow_drop_down</v-icon>
                                <v-icon v-show="menu_status">arrow_drop_up</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-tile  v-for="(item,index) in myTeamsVisibleInMenu" :key="index" @click="setSelectedTeam(item)">
                                <v-list-tile-title small>{{item.name}}</v-list-tile-title>
                            </v-list-tile>
                            <v-list-tile v-if="myTeams.length > maxVisibleMenuItems" @click="viewAllTeams" title="Click to view all teams">
                                <v-list-tile-title small class="text-xs-center">
                                    <b>View All <v-icon small>open_in_new</v-icon></b> 
                                </v-list-tile-title>
                            </v-list-tile>
                        </v-list>
                    </v-menu>
                </v-toolbar>
                <v-progress-linear v-if="loading" indeterminate color="primary" height="2" class="mt-0"></v-progress-linear>                
            </v-card>

            <v-alert v-if="error" :value="true" color="error" icon="error" outline class="mt-8">
                Error encountered. Please contact support@sweldonow.ph, Details: {{ errorMessage }}
            </v-alert>  

            <v-layout row wrap>
                <v-flex xs12 sm9 class="roles-panel">

                    <!-- TEAM MEMBERS -->
                    <v-card class="elevation-1 mt-5">
                        <v-toolbar flat height="37" @click="teamMembersVisible=!teamMembersVisible" class="clickable">
                            <span class="primary--text font-weight-bold">
                                MEMBERS
                            </span>
                            <v-spacer></v-spacer>
                            <v-btn icon :title="teamMembersVisible ? 'Minimize' : 'Expand'">
                                <v-icon>{{ teamMembersVisible ? 'expand_less' : 'expand_more' }}</v-icon>
                            </v-btn>
                        </v-toolbar>                        
                        <v-card-text v-if="teamMembersVisible">
                            <div v-if="!loading">
                                <div v-for="(item,index) in roles" :key="index">

                                    <!-- Members Chips with collapse/expand -->
                                    <div v-if="(members = getTeamMembers(item.teamRoleId)) && members.length" class="chips-container">

                                        <div :class="hasEditRightsToTeamRolesPage?'role-name':''" @click="navigateToTeamRolesPage(item.teamRoleId)" :title="hasEditRightsToTeamRolesPage?`Click to view ${item.name} permissions`:''">{{ item.name }}</div>

                                        <v-chip
                                            v-for="(member, idx) in (isExpanded(item.teamRoleId) 
                                                ? members 
                                                : members.slice(0, maxVisible))"
                                            :key="idx"
                                            @click="viewUserProfile(member.employee.userId)"
                                        >
                                            <v-avatar>
                                                <v-img :src="awsS3Helper.getAvatarUrl(member.employee.userId)"></v-img>
                                            </v-avatar>
                                            <span class="chips-item">
                                                {{ member.employee.firstName }} {{ member.employee.lastName }}
                                            </span>
                                        </v-chip>

                                        <!-- View more -->
                                        <div v-if="!isExpanded(item.teamRoleId) && members.length > maxVisible"
                                             class="view-more-link"
                                             @click="toggleRole(item.teamRoleId)">
                                            View {{ members.length - maxVisible }} more
                                        </div>

                                        <!-- Hide others -->
                                        <div v-if="isExpanded(item.teamRoleId) && members.length > maxVisible"
                                             class="view-more-link"
                                             @click="toggleRole(item.teamRoleId)">
                                            Hide others
                                        </div>
                                    </div>
                                </div>                               
                            </div>
                        </v-card-text>
                    </v-card>

                    <!-- TEAM EVENTS -->
                    <upcoming-team-events ref="upcomingEventsRef" class="mt-5"></upcoming-team-events>

                    <!-- TEAM STATUS TODAY -->
                    <team-status-today ref="teamStatusRef" class="mt-5"></team-status-today>

                </v-flex>

                <!-- Quick Links -->
                <v-flex xs12 sm3 class="links-panel">
                    <v-card class="elevation-1 sidebar-card">
                        <v-toolbar flat height="37" @click="teamLinksVisible=!teamLinksVisible">
                            <span class="primary--text font-weight-bold">
                                QUICK LINKS
                            </span>
                            <v-spacer></v-spacer>
                            <v-btn v-if="isMobile&&teamLinksVisible" icon title="Minimize">
                                <v-icon>expand_less</v-icon>
                            </v-btn>
                            <v-btn v-if="isMobile&&!teamLinksVisible" icon title="Expand">
                                <v-icon>expand_more</v-icon>
                            </v-btn>
                        </v-toolbar>
                        <v-navigation-drawer v-if="!isMobile||teamLinksVisible" floating permanent stateless value="true">
                            <v-list dense>
                                <v-list-tile v-for="item in myTeamsPages" :key="item.title" @click="$router.push(item.pagePath)">
                                    <v-list-tile-action>
                                        <v-icon>{{ item.pageIcon }}</v-icon>
                                    </v-list-tile-action>
                                    <v-list-tile-content>
                                        <v-list-tile-title>{{ item.pageName }}</v-list-tile-title>
                                    </v-list-tile-content>
                                </v-list-tile>
                            </v-list>
                        </v-navigation-drawer>
                    </v-card>
                </v-flex>

            </v-layout>
        </v-flex>

        <!-- All Teams Dialog -->
        <v-dialog v-model="dialogAllMyTeams" width="700">   
            <view-all-my-teams ref="allTeamsRef" @send="setSelectedTeam" :dialogAllMyTeams.sync="dialogAllMyTeams"></view-all-my-teams>
        </v-dialog>
    </v-layout> 
</template>

<script>

import pageIds from '@/constants/app_ids'
import teamApi from '@/api/team'
import awsS3Helper from '@/helpers/aws-s3'
import { hasEditRights } from '@/helpers/access-permissions'
import { getExpandCollapseVisibility, updateExpandCollapseVisibility } from '@/helpers/user-prefs'
import { setSelectedTeam } from '@/helpers/team-selector'

export default {     
    name: 'MyTeamsMenu',
    components: {
        'view-all-my-teams': () => import('@/components/Team/ViewAllTeams'),
        'team-status-today': () => import('@/components/Team/TeamStatusToday'),
        'upcoming-team-events': () => import('@/components/Team/UpcomingTeamEvents'),
    },
    data () {
        const routeName = this.$route.name

        return {
            awsS3Helper,
            teamMembersVisible: getExpandCollapseVisibility(routeName, 'my-teams-team-members'),
            teamLinksVisible: getExpandCollapseVisibility(routeName, 'my-teams-links'),
            error: false,
            errorMessage: null,
            loading: false,
            menu_status: false,
            dialogAllMyTeams: false,
            maxVisibleMenuItems: 5,
            maxVisible: 5,            
            expandedRoles: {},        
            myTeams: [],
            roles: [],
            events_list: [],
            selectedTeamId: null 
        }
    },
    methods: {
        navigateToTeamRolesPage(teamRoleId) {
            if (this.hasEditRightsToTeamRolesPage) {
                this.$router.push(`/settings/teamroles/${teamRoleId}`)
            }
        },
        toggleRole(roleId) {
            this.$set(this.expandedRoles, roleId, !this.expandedRoles[roleId]);
        },
        isExpanded(roleId) {
            return !!this.expandedRoles[roleId];
        },
        viewAllTeams() {
            if (this.$refs.allTeamsRef) {
                this.$refs.allTeamsRef.setData(this.myTeams)
                this.dialogAllMyTeams = true
            }
        },
        setSelectedTeam(data){
            if (!data || !data.teamId) return 

            this.selectedTeamId = data.teamId
            setSelectedTeam(data)

            this.$nextTick(() => {
                if (this.$refs.upcomingEventsRef && typeof this.$refs.upcomingEventsRef.getEvents === 'function') {
                    this.$refs.upcomingEventsRef.getEvents(data.teamId)
                }
                if (this.$refs.teamStatusRef && typeof this.$refs.teamStatusRef.getTeamStatusToday === 'function') {
                    this.$refs.teamStatusRef.getTeamStatusToday(data.teamId)
                }
            })
        },
        viewUserProfile(userId){
            if (userId) this.$router.push(`/hr/employees/${userId}`)
        },
        getTeamMembers(roleId){
            if (!this.selectedTeamData || !Array.isArray(this.selectedTeamData.members)) {
                return []
            }
            return this.selectedTeamData.members.filter(x => x && x.visibleInMyTeams && x.roleId == roleId)
        },
        async getMyTeams() {
            try {
                this.loading = true
                this.error = false
                const response = await teamApi.getMyTeams()
                
                if (response && response.status == 200 && response.data) {
                    this.myTeams = response.data.myTeams || []
                    this.roles = response.data.roles || []

                    if (this.myTeams.length > 0) {
                        if (!this.savedSelectedTeamId) {
                            this.setSelectedTeam(this.myTeams[0])
                        } else {
                            const selectedTeamData = this.myTeams.find(x => x.teamId == this.savedSelectedTeamId)
                            this.setSelectedTeam(selectedTeamData || this.myTeams[0])
                        }
                    }
                }
            } catch(error) {
                this.error = true
                // FIX: Replaced optional chaining with traditional ternary logic
                this.errorMessage = (error && error.message) ? error.message : error
            } finally {
                this.loading = false
            }
        },
    },
    async created() {
        this.$root.$emit('setHeaderTitles', {
            display: true,
            isMainMenu: true,
            headerTitle: 'My Teams',
            headerPageTitle: this.isMobile ? '' : 'Menu'
        })

        await this.getMyTeams()
    },
    watch: {
        teamMembersVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'my-teams-team-members', newVal)
        },
        teamLinksVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'my-teams-links', newVal)
        }
    },
    computed: {
        hasEditRightsToTeamRolesPage(){
            const router = this.$router.resolve({ name: 'TeamRoles' })
            return hasEditRights(router.route.fullPath)
        },
        selectedTeamData() {
            if (!this.myTeams || !this.myTeams.length) return null

            const selectedData = this.myTeams.filter(x => x.teamId == this.savedSelectedTeamId)
            return selectedData.length ? selectedData[0] : this.myTeams[0]
        },
        savedSelectedTeamId() {
            if (!this.$store || !this.$store.state) return null

            const selectedTeamId = this.$store.state.selectedTeamId
            const selectedTeamCompanyId = this.$store.state.selectedTeamCompanyId
            const loggedInCompanyId = this.$store.state.companyId

            if (selectedTeamId && selectedTeamCompanyId === loggedInCompanyId) {
                return selectedTeamId
            }
            return null
        },
        myTeamsVisibleInMenu() {
            return Array.isArray(this.myTeams) ? this.myTeams.slice(0, this.maxVisibleMenuItems) : []
        },
        myTeamsPages() {
            // FIX: Removed optional chaining safely with standard logical validations
            if (this.selectedTeamData && Array.isArray(this.selectedTeamData.permissions) && this.$store && this.$store.state && this.$store.state.user_apps_list) {
                const app = this.$store.state.user_apps_list.find(x => x && x.appId === pageIds.my_team)
                const teamPages = app && app.appPages ? app.appPages : []

                return teamPages.filter(page => {
                    if (!page) return false
                    const perm = this.selectedTeamData.permissions.find(p => p && p.appPageId === page.appPageId)
                    return perm && perm.view
                })
            }
            return []
        }
    }
}
</script>

<style scoped>
/* Flexbox ordering - desktop view */
.roles-panel { order: 1; }
.links-panel { order: 2; }

/* Flexbox ordering - mobile view */
@media (max-width: 600px) {
  .roles-panel { order: 2; }
  .links-panel { order: 1; }
}

.chips-container {
    margin: 0px 0px 20px 10px;
}
.chips-container > div {
    font-weight: bold;
}
.chips-item {
    cursor: pointer;
}

/* View more / hide link */
.view-more-link {
    color: #1976d2;
    cursor: pointer;
    margin-top: 6px;
    margin-left: 4px;
    font-size: 14px;
}
.view-more-link:hover {
    text-decoration: underline;
}
.sidebar-card {
    margin-top: 5px;
}
.role-name {
    text-decoration: underline;
}
.role-name:hover{
    cursor:pointer;
}
</style>
